// The operator's own books: a backup they hold, and the day before today.
//
// Two things a console needs before anybody runs a business on it, and
// neither of them is a server.
//
// **A backup.** Everything this app knows lives in one browser's
// `localStorage`: the signing seed that issues jobs, the roster of
// enrolled phones, every route root, and the record of what went where.
// A cleared browser is a fleet that has lost its identity — new issuer
// seed, every enrolled phone a stranger, every route root gone and with
// it every term link already given to a parent. There is no server to
// restore from, by design, so the operator has to be able to hold a copy.
//
// **Yesterday.** The board is today's work and stays that way; a day
// that is over is a different question — "what happened at that address
// last Tuesday" — and answering it is a search, not a board.
//
// The file this writes is **the keys themselves in the clear**. That is
// stated on the button rather than buried: a copy that could not restore
// the operation would be a comfort blanket, and pretending otherwise is
// how people find out at the worst moment. Where it is kept is the
// operator's decision, and the screen says what it is worth.

import { useMemo, useRef, useState } from "react";
import { STOP_OUTCOME, type FollowSnapshot } from "@wayfind/core";
import { Note, Panel, downloadFile } from "./Bits";
import { backupFilename, encodeBackup, readBackup } from "../ops/backup";
import { dateWords, plural, type Vocabulary } from "../ops/words";
import type { FleetState, StoredJob } from "../ops/state";
import { nowSeconds } from "../config";

const DAY = 86400;

export function Books({
  state,
  vocabulary,
  live,
  onRestore,
  onOpen
}: {
  state: FleetState;
  vocabulary: Vocabulary;
  live: Map<string, FollowSnapshot>;
  onRestore(state: FleetState): void;
  onOpen(job: StoredJob): void;
}): JSX.Element {
  const now = nowSeconds();
  const [query, setQuery] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [restored, setRestored] = useState<string | null>(null);
  const file = useRef<HTMLInputElement | null>(null);

  // Everything older than the board's window. The board owns today, and
  // two surfaces answering the same question is how they disagree.
  const earlier = useMemo(
    () => state.jobs
      .filter(job => now - job.createdUnixSeconds >= DAY)
      .slice()
      .sort((a, b) => b.createdUnixSeconds - a.createdUnixSeconds),
    [state.jobs, now]
  );

  const found = useMemo(() => {
    const text = query.trim().toLowerCase();
    if (!text) return earlier.slice(0, 12);
    return earlier.filter(job =>
      job.stops.some(stop => stop.label.toLowerCase().includes(text))
      || (job.driverName ?? "").toLowerCase().includes(text)
      || job.jobIdHex.startsWith(text)
    ).slice(0, 40);
  }, [earlier, query]);

  const take = (): void => {
    downloadFile(backupFilename(now), encodeBackup(state, now), "application/json");
  };

  const read = async (chosen: File | null): Promise<void> => {
    setProblem(null);
    setRestored(null);
    if (!chosen) return;
    const result = readBackup(await chosen.text());
    if (!result.ok) {
      setProblem(result.reason);
      return;
    }
    onRestore(result.state);
    setRestored(
      `Restored ${plural(result.state.jobs.length, vocabulary.job)} and `
      + `${plural(result.state.devices.length, "phone")}.`
    );
  };

  return (
    <Panel eyebrow="The books">
      <div className="row">
        <button type="button" className="button" onClick={take}>Save a backup</button>
        <button type="button" className="button" onClick={() => file.current?.click()}>
          Restore from a file
        </button>
        <input
          ref={file}
          type="file"
          accept=".fleetbackup,application/json"
          style={{ display: "none" }}
          onChange={event => { void read(event.target.files?.[0] ?? null); }}
        />
      </div>

      <Note title="This file is the operation, keys and all." tone="signal">
        It carries the seed that signs your {vocabulary.jobs}, the phones you have paired, and every{" "}
        {vocabulary.route} root — which is what a term link is made from. Anybody holding it can
        issue work in your name, so keep it where you keep the till float, not in a shared inbox.
        There is no server holding a second copy: a cleared browser without this file is a fleet
        that has lost its identity, and every link already given out with it.
      </Note>

      {problem ? (
        <div className="banner banner--halt" role="alert" style={{ marginTop: "0.6rem" }}>
          <span className="banner__mark">No</span>
          <p className="banner__text">{problem}</p>
        </div>
      ) : null}
      {restored ? (
        <div className="banner" role="status" style={{ marginTop: "0.6rem" }}>
          <span className="banner__mark">Done</span>
          <p className="banner__text">{restored}</p>
        </div>
      ) : null}

      {earlier.length ? (
        <>
          <hr className="divider" />
          <label className="field">
            <span className="field__label">Before today</span>
            <input
              className="input"
              value={query}
              placeholder={`An address, a ${vocabulary.driver}, or a job id`}
              onChange={event => setQuery(event.target.value)}
            />
          </label>
          <div className="rows" style={{ marginTop: "0.5rem" }}>
            {found.map(job => {
              const snapshot = live.get(job.id) ?? null;
              const outcomes = snapshot?.status.outcomes ?? [];
              const delivered = outcomes.filter(o => o === STOP_OUTCOME.DELIVERED).length;
              const missed = outcomes.filter(
                o => o === STOP_OUTCOME.SKIPPED || o === STOP_OUTCOME.FAILED
              ).length;
              return (
                <button
                  key={job.id}
                  type="button"
                  className="rowcard"
                  style={{ textAlign: "start", cursor: "pointer", font: "inherit", color: "inherit" }}
                  onClick={() => onOpen(job)}
                >
                  <div className="rowcard__main">
                    <span className="rowcard__name">{job.stops[0]?.label ?? "A job"}</span>
                    <span className="rowcard__sub">
                      {dateWords(job.createdUnixSeconds)}
                      {job.stops.length > 1 ? ` · ${plural(job.stops.length, vocabulary.stop)}` : ""}
                      {job.driverName ? ` · ${job.driverName}` : ""}
                      {/*
                        Counts only where this console actually re-read the
                        run. A settled job is not followed any more, so an
                        absent count means "not re-read", never "nothing
                        was delivered" — which is why nothing is drawn at
                        all rather than a zero.
                      */}
                      {delivered || missed
                        ? ` · ${delivered} delivered${missed ? `, ${missed} not` : ""}`
                        : ""}
                    </span>
                  </div>
                </button>
              );
            })}
            {!found.length ? (
              <p className="small muted">Nothing before today matches that.</p>
            ) : null}
          </div>
        </>
      ) : null}
    </Panel>
  );
}
